import React from 'react';
import { PreviewMode } from '../../preview-elements/preview-context';
import {
  EmailSmall,
  AdminAccessSmall,
  PublicSmall,
} from '@wix/wix-ui-icons-common';
import s from './solution-overview.scss';
import { composer } from '../../../../component-decorators/composer';
import type { WithTranslation as WithTranslationProps } from 'react-i18next';
import { IconPreviewWrapper } from '../../preview-elements/icon-wrapper';
import {
  SupportLinkPreviewWrapper,
  PrivacyLinkPreviewWrapper,
  DevSiteLinkWrapper,
} from '../../preview-elements/developer-card-wrappers';
import { DevelopedByWrapper } from '../../preview-elements';
import { biLogger } from '../../../../bi';
import { Text, Box, Avatar, Card, TextButton } from '@wix/design-system';
import { getClientImageURL } from '../../../../common/getClientImageURL';
import type { InjectedExperimentsProps } from '@wix/wix-experiments-react';
import { ModalNames } from '../../../modals/modals';
import { useConfigContext } from '../../../../config-context';
import { isWixApp, wixChatBotLink } from '../../../../common/utils';
import type {
  WebSolution,
  WebSolutionBase,
} from '@wix/ambassador-marketplace/types';
import type { MarketplaceStore } from '../../../../component-decorators/with-marketplace-store';
import { appMarketDeveloperContactBoxActionClick } from '@wix/bi-logger-market/v2';

export interface IDeveloperCardProps
  extends Partial<WithTranslationProps>,
    Partial<InjectedExperimentsProps> {
  marketplaceStore?: MarketplaceStore;
  name: string;
  icon: string;
  id: string;
  supportEmail: string;
  website: string;
  privacyPolicyUrl?: string;
  baseInfo?: WebSolutionBase;
  assetsMap?: any;
  company?: any;
  webSolutionData?: any;
}

export const dtoToDevCardProps = (
  solutionDto: WebSolution,
): IDeveloperCardProps => {
  const baseInfo = solutionDto.baseInfo;
  const assetsMap = solutionDto.assetsMap;
  const company = solutionDto.company;
  const webSolutionData = solutionDto.webSolutionData;
  const supportEmail =
    solutionDto.supportEmails && solutionDto.supportEmails.join(';');
  const website = solutionDto.company ? solutionDto.company.website : undefined;
  const name = solutionDto.company
    ? solutionDto.company.companyBase.name
    : 'Dev Name'; // TODO FIX
  const icon =
    solutionDto.company &&
    solutionDto.company.assetsMap &&
    solutionDto.company.assetsMap.companyIcon &&
    solutionDto.company.assetsMap.companyIcon.assets &&
    solutionDto.company.assetsMap.companyIcon.assets[0].url;
  const id = solutionDto.baseInfo.id;
  const privacyPolicyUrl = solutionDto.company.privacyPolicyUrl;
  return {
    website,
    name,
    icon,
    supportEmail,
    privacyPolicyUrl,
    id,
    baseInfo,
    assetsMap,
    company,
    webSolutionData,
  };
};
export const DeveloperCard = composer()
  .withTranslation()
  .withMarketplaceStore()
  .withExperiments()
  .compose((props: IDeveloperCardProps) => {
    const appFromInstalled =
      props.marketplaceStore.installedApps &&
      props.marketplaceStore.installedApps?.find(
        (installedApp) => installedApp.appId === props.baseInfo.id,
      );

    const madeByWix =
      props.company &&
      props.company.companyBase &&
      isWixApp(props.company.companyBase.id);

    const { shouldUseSupportModal } = useConfigContext();

    return (
      <PreviewMode.Consumer>
        {(value) => {
          return value.isPreview || haveSomeLink(props) ? (
            <Card className={s.sideCardContainer}>
              <div data-testid="developer-card" />
              <DevelopedBy icon={props.icon} name={props.name} />
              <Card.Divider />
              <Box
                paddingTop="18px"
                paddingBottom="18px"
                paddingRight="24px"
                paddingLeft="24px"
                direction="vertical"
              >
                <DeveloperSiteLink
                  href={props.website}
                  onClick={() =>
                    sendDeveloperContactBoxActionClickBi(
                      'visit_website',
                      props.id,
                      props.marketplaceStore.metaSiteId,
                    )
                  }
                />
                {madeByWix ? (
                  <WixChatBotLink
                    href={wixChatBotLink}
                    onClick={() =>
                      sendDeveloperContactBoxActionClickBi(
                        'contact_support',
                        props.id,
                        props.marketplaceStore.metaSiteId,
                      )
                    }
                  />
                ) : shouldUseSupportModal && appFromInstalled ? (
                  <DeveloperEmailLinkWithSupportModal
                    href={props.supportEmail}
                    onClick={() => {
                      sendDeveloperContactBoxActionClickBi(
                        'contact_support',
                        props.id,
                        props.marketplaceStore.metaSiteId,
                      );
                      props.marketplaceStore.openModal(
                        ModalNames.SUPPORT_MODAL,
                        {
                          app: appFromInstalled
                            ? appFromInstalled
                            : (props as WebSolution),
                          closeModal: () =>
                            props.marketplaceStore.closeAllModals(),
                          clickSource: 'developerEmailLink',
                          pageName: 'app_page',
                        },
                      );
                    }}
                  />
                ) : (
                  <DeveloperEmailLink
                    href={props.supportEmail}
                    onClick={() =>
                      sendDeveloperContactBoxActionClickBi(
                        'contact_support',
                        props.id,
                        props.marketplaceStore.metaSiteId,
                      )
                    }
                  />
                )}
                <DeveloperPrivacyPolicyPage
                  href={props.privacyPolicyUrl}
                  onClick={() =>
                    sendDeveloperContactBoxActionClickBi(
                      'privacy',
                      props.id,
                      props.marketplaceStore.metaSiteId,
                    )
                  }
                />
              </Box>
            </Card>
          ) : (
            <div />
          );
        }}
      </PreviewMode.Consumer>
    );
  });

const haveSomeLink = (props: IDeveloperCardProps) => {
  if (props) {
    const { website, supportEmail, privacyPolicyUrl } = props;
    return website || supportEmail || privacyPolicyUrl;
  }
  return false;
};

const sendDeveloperContactBoxActionClickBi = (actionName, appId, msid) => {
  biLogger.report(
    appMarketDeveloperContactBoxActionClick({
      actionName,
      app_id: appId,
      msid,
      tag_name: appId,
      tag_type: 'solution_page',
    }),
  );
};

interface IDevelopedByProps extends Partial<WithTranslationProps> {
  name: string;
  icon: string;
}

const DevelopedBy = composer()
  .withTranslation()
  .compose((props: IDevelopedByProps) => {
    return (
      <Box verticalAlign="middle" className={s.devCardBy}>
        <IconPreviewWrapper size="62px" src={props.icon}>
          {props.icon ? (
            <Avatar
              size="size36"
              imgProps={{
                src: getClientImageURL(props.icon, {
                  toHeight: 36,
                  toWidth: 36,
                }),
                alt: `developed by ${props.name}`,
              }}
            />
          ) : (
            <Avatar size="size48" name="Cohav Nofar" color="A1" />
          )}
        </IconPreviewWrapper>
        <Box marginLeft="12px" direction="vertical">
          <Text size="small" weight="thin" secondary>
            {props.t('solution.page.devCardBy')}
          </Text>
          <DevelopedByWrapper name={props.name}>
            <Text size="medium" weight="bold" dataHook="dev-card-name">
              {props.name}
            </Text>
          </DevelopedByWrapper>
        </Box>
      </Box>
    );
  });

interface IDevCardLinkProps extends Partial<WithTranslationProps> {
  href: string;

  onClick(): void;
}

const DeveloperSiteLink = composer()
  .withTranslation()
  .compose((props: IDevCardLinkProps) => {
    return (
      <DevSiteLinkWrapper href={props.href}>
        {props.href ? (
          <Box
            verticalAlign="middle"
            paddingBottom={1}
            className={s.devCardLinkContainer}
          >
            <TextButton
              size="small"
              dataHook="developer-website-link"
              prefixIcon={<PublicSmall />}
              as="a"
              href={props.href}
              target="_blank"
              onClick={() => props.onClick()}
            >
              {props.t('solution.page.devCardVisitLink')}
            </TextButton>
          </Box>
        ) : (
          <div />
        )}
      </DevSiteLinkWrapper>
    );
  });
const DeveloperEmailLink = composer()
  .withTranslation()
  .compose((props: IDevCardLinkProps) => {
    return (
      <SupportLinkPreviewWrapper href={props.href}>
        {props.href ? (
          <Box
            verticalAlign="middle"
            paddingTop={1}
            paddingBottom={1}
            className={s.devCardLinkContainer}
          >
            <TextButton
              size="small"
              dataHook="developer-email-link"
              prefixIcon={<EmailSmall />}
              as="a"
              href={`mailto:${props.href}`}
              onClick={() => props.onClick()}
            >
              {props.t('solution.page.devCardContactUs')}
            </TextButton>
          </Box>
        ) : (
          <div />
        )}
      </SupportLinkPreviewWrapper>
    );
  });

const WixChatBotLink = composer()
  .withTranslation()
  .compose((props: IDevCardLinkProps) => {
    return (
      <SupportLinkPreviewWrapper href={props.href}>
        <Box
          verticalAlign="middle"
          paddingTop={1}
          paddingBottom={1}
          className={s.devCardLinkContainer}
        >
          <TextButton
            size="small"
            dataHook="developer-email-link"
            prefixIcon={<EmailSmall />}
            as="a"
            href={props.href}
            onClick={() => props.onClick()}
            target="_blank"
          >
            {props.t('solution.page.devCardContactUs')}
          </TextButton>
        </Box>
      </SupportLinkPreviewWrapper>
    );
  });

const DeveloperEmailLinkWithSupportModal = composer()
  .withTranslation()
  .compose((props: IDevCardLinkProps) => {
    return (
      <SupportLinkPreviewWrapper href={props.href}>
        <Box
          verticalAlign="middle"
          paddingTop={1}
          paddingBottom={1}
          className={s.devCardLinkContainer}
        >
          <TextButton
            size="small"
            dataHook="developer-email-link"
            prefixIcon={<EmailSmall />}
            as="a"
            onClick={() => props.onClick()}
          >
            {props.t('solution.page.devCardContactUs')}
          </TextButton>
        </Box>
      </SupportLinkPreviewWrapper>
    );
  });

const DeveloperPrivacyPolicyPage = composer()
  .withTranslation()
  .compose((props: IDevCardLinkProps) => {
    return (
      <PrivacyLinkPreviewWrapper href={props.href}>
        {props.href ? (
          <Box
            verticalAlign="middle"
            paddingTop={1}
            className={s.devCardLinkContainer}
          >
            <TextButton
              size="small"
              dataHook="developer-privacy-link"
              prefixIcon={<AdminAccessSmall />}
              as="a"
              href={props.href}
              target="_blank"
              onClick={() => props.onClick()}
            >
              {props.t('solution.page.devCardPrivacyLink')}
            </TextButton>
          </Box>
        ) : (
          <div />
        )}
      </PrivacyLinkPreviewWrapper>
    );
  });
